import { RangeHorizon, ResampleIntervals } from '@toggle/chart';
import { Button } from '@toggle/design-system';
import { Entity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useChartNavigate } from '~/hooks/use-chart-navigate/useChartNavigate';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { useEntities } from '~/react-query-hooks/entities/useEntities';
import { queryKeys } from '~/routes/query-params';
import { ChartSearchParamsAssetValue } from '~/utils/chart-search-utils/chart-search-utils';
import { ChartPeerGroup } from '~/widgets/chart-peer-group/ChartPeerGroup';

import * as S from './RelativePerformance.styles';

const SPX_TAG = 'spx';

export const RelativePerformance = ({ entity }: { entity: Entity }) => {
  const { t } = useTranslation(['widget']);
  const chartNavigate = useChartNavigate();
  const { params } = useQueryParams();
  const competitorTag = params[queryKeys.competitorTag];

  const revampCompetitorTags = [
    ...(competitorTag ? [competitorTag] : []),
    SPX_TAG,
  ];

  const {
    data: competitorEntitiesData = [],
    isLoading: isLoadingCompetitorEntitiesData,
  } = useEntities({
    entityTags: revampCompetitorTags,
    enabled: !!revampCompetitorTags.length,
  });

  const peerEntities = revampCompetitorTags
    .map(tag => competitorEntitiesData.find(entity => entity.tag === tag))
    .filter(x => !!x);

  const onClick = () => {
    const assets: ChartSearchParamsAssetValue[] = [entity, ...peerEntities].map(
      asset => [asset.tag, asset.default_snake, 0]
    );

    chartNavigate(assets, {
      horizon: RangeHorizon.OneYear,
      resample: ResampleIntervals.OneDay,
      yAxisType: 'merged',
    });
  };

  return (
    <S.SectionRow>
      <S.HeaderWrapper>
        <S.TitleBase>
          {t(
            'assetOverview.knowledgeGraph.competitors.tabs.competitorOverview.relativePerformance'
          )}
        </S.TitleBase>
        <S.FullscreenButtonContainer>
          <Button
            variant="empty"
            iconName="Expand"
            size="small"
            onClick={onClick}
            data-testid="open-full-chart"
          />
        </S.FullscreenButtonContainer>
      </S.HeaderWrapper>
      <S.ChartWrapper>
        <ChartPeerGroup
          entity={entity}
          variant="kg_competitors_drawer"
          isFetching={isLoadingCompetitorEntitiesData}
          peerEntities={peerEntities}
        />
      </S.ChartWrapper>
    </S.SectionRow>
  );
};
